import type {
  TopicContentDisclaimerType,
  TopicContentJurisdiction,
} from "./topic-content-manifest";

export type TopicContentDisclaimerLocale = "ko" | "en";

export type TopicContentDisclaimerInput = {
  disclaimerType: TopicContentDisclaimerType;
  lastReviewed: string;
  jurisdictions: readonly TopicContentJurisdiction[];
  sourceTrustLabel: string;
};

export type TopicContentDisclaimerText = {
  title: string;
  lastReviewedLabel: string;
  jurisdictionLabel: string;
  sourceTrustLabel: string;
  body: string;
  consult: string;
};

const DISCLAIMER_CONTEXT = {
  tax: {
    ko: "세무",
    en: "tax",
    professionalKo: "등록된 세무사나 회계사",
    professionalEn: "a registered tax agent or accountant",
  },
  legal: {
    ko: "법률",
    en: "legal",
    professionalKo: "변호사, 관련 tribunal 또는 정부기관",
    professionalEn: "a lawyer, relevant tribunal, or government agency",
  },
  migration: {
    ko: "이민",
    en: "migration",
    professionalKo: "등록된 이민법무사, 변호사 또는 Home Affairs",
    professionalEn:
      "a registered migration agent, lawyer, or Home Affairs",
  },
  medical: {
    ko: "의료",
    en: "medical",
    professionalKo: "의료전문가, Services Australia 또는 관련 정부기관",
    professionalEn:
      "a medical professional, Services Australia, or the relevant government agency",
  },
  financial: {
    ko: "금융",
    en: "financial",
    professionalKo: "자격을 갖춘 금융, 세무, 법률 전문가",
    professionalEn: "a qualified financial, tax, or legal professional",
  },
  general_high_risk: {
    ko: "고위험 생활정보",
    en: "high-risk general information",
    professionalKo: "자격을 갖춘 전문가 또는 관련 정부기관",
    professionalEn: "a qualified professional or the relevant government agency",
  },
} as const satisfies Record<
  TopicContentDisclaimerType,
  {
    ko: string;
    en: string;
    professionalKo: string;
    professionalEn: string;
  }
>;

const JURISDICTION_LABELS = {
  australia: {
    ko: "호주",
    en: "Australia",
  },
  nsw: {
    ko: "NSW",
    en: "NSW",
  },
  state_required: {
    ko: "주/준주별 확인 필요",
    en: "State or territory confirmation required",
  },
} as const satisfies Record<
  TopicContentJurisdiction,
  { ko: string; en: string }
>;

export function getTopicContentDisclaimerText(
  input: TopicContentDisclaimerInput,
  locale: TopicContentDisclaimerLocale,
): TopicContentDisclaimerText {
  const context = DISCLAIMER_CONTEXT[input.disclaimerType];
  const jurisdictions = formatJurisdictions(input.jurisdictions, locale);

  if (locale === "en") {
    return {
      title: "Important information",
      lastReviewedLabel: `Last reviewed: ${input.lastReviewed}`,
      jurisdictionLabel: `Jurisdiction: ${jurisdictions}`,
      sourceTrustLabel: `Source trust: ${input.sourceTrustLabel}`,
      body: `This content is general ${context.en} information only and is not professional advice. Your outcome may differ depending on your visa status, income, employment type, family situation, location, timing, and other personal circumstances.`,
      consult: `Before making important decisions, confirm the information with ${context.professionalEn} and the relevant official source.`,
    };
  }

  return {
    title: "중요 안내",
    lastReviewedLabel: `정보 업데이트: ${input.lastReviewed}`,
    jurisdictionLabel: `적용 지역: ${jurisdictions}`,
    sourceTrustLabel: `출처 신뢰도: ${input.sourceTrustLabel}`,
    body: `이 글은 일반적인 ${context.ko} 정보 제공을 위한 것이며 전문 자문이 아닙니다. 비자 상태, 소득, 고용 형태, 가족 상황, 거주 지역, 시점과 개인 상황에 따라 실제 결과가 달라질 수 있습니다.`,
    consult: `중요한 결정을 하기 전에는 반드시 ${context.professionalKo}와 관련 공식 출처에 확인하세요.`,
  };
}

export function formatJurisdictions(
  jurisdictions: readonly TopicContentJurisdiction[],
  locale: TopicContentDisclaimerLocale,
): string {
  return jurisdictions
    .map((jurisdiction) => JURISDICTION_LABELS[jurisdiction][locale])
    .join(locale === "en" ? ", " : ", ");
}
